// i18n: translated
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { useT } from '@/i18n';
import { docusignNs } from '@/i18n/namespaces/docusign';
import { completeDocuSignAuthorization, useSelectDocuSignAccount } from '@/hooks/useDocuSign';
import { takeDocuSignCallback } from '@/utils/docusignOAuth';
import type { DocuSignAccount, DocuSignAccountSelection } from '@/types/api-types';
import { ApiResponseError } from '@/api/client';

/** 認証前の redirect に code/state を載せない、callback 専用の認証 guard。 */
export default function DocuSignCallbackPage() {
  const t = useT(docusignNs);
  const { user, loading } = useAuth();
  const permission = usePermission();
  const writable = permission.canWrite('integrations');
  const request = useRef<Promise<DocuSignAccountSelection> | null>(null);
  const [accounts, setAccounts] = useState<DocuSignAccount[] | null>(null);
  const [account, setAccount] = useState('');
  const [error, setError] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const select = useSelectDocuSignAccount();
  useEffect(() => {
    if (loading || permission.status === 'loading') return;
    if (!user || !writable) {
      takeDocuSignCallback();
      return;
    }
    if (!request.current) {
      const input = takeDocuSignCallback();
      if (!input) {
        setError(true);
        return;
      }
      request.current = completeDocuSignAuthorization(input);
    }
    let active = true;
    void request.current
      .then((result) => {
        if (active) setAccounts(result.accounts);
      })
      .catch((error: unknown) => {
        if (active) {
          setError(true);
          if (error instanceof ApiResponseError && error.status === 401) setSessionExpired(true);
        }
      });
    return () => {
      active = false;
    };
  }, [loading, user, writable, permission.status]);
  return (
    <main className="mx-auto max-w-xl space-y-4 p-8">
      <h1 className="text-xl font-semibold">{t('callbackTitle')}</h1>
      {loading || permission.status === 'loading' ? (
        <p>{t('loading')}</p>
      ) : !user || sessionExpired ? (
        <>
          <p role="alert">{t('loginAgain')}</p>
          <a href="/login">{t('login')}</a>
        </>
      ) : !writable ? (
        <p role="alert">{t('noPermission')}</p>
      ) : connected ? (
        <p role="status">{t('connected')}</p>
      ) : (
        <>
          {error ? (
            <p role="alert">{t('restart')}</p>
          ) : accounts === null ? (
            <p>{t('callbackWait')}</p>
          ) : (
            <>
              <p>{t('accountHelp')}</p>
              <label className="block">
                {t('account')}
                <select
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  disabled={select.loading}
                  className="mt-1 block w-full rounded border bg-transparent p-2"
                >
                  <option value="">{t('selectAccount')}</option>
                  {accounts.map((a) => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.accountName} ({a.accountId})
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                disabled={!account || select.loading}
                onClick={async () => {
                  if (!user || !writable || !accounts?.some((a) => a.accountId === account)) return;
                  const result = await select.mutate(account);
                  if (result?.connected) setConnected(true);
                  else setError(true);
                }}
                className="rounded bg-indigo-600 px-4 py-2 text-white disabled:opacity-50"
              >
                {select.loading ? t('saving') : t('confirmAccount')}
              </button>
            </>
          )}
        </>
      )}
      <p>
        <Link to="/settings/org?tab=integrations">{t('settings')}</Link>
      </p>
    </main>
  );
}
